import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Typography, Layout, List, Divider } from 'antd';
import { ReloadOutlined, DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { Content, Sider } = Layout;

interface LogFile {
    name: string;
    size: number;
    updatedAt: string;
}

const LogViewer: React.FC = () => {
    const [logs, setLogs] = useState<LogFile[]>([]);
    const [selectedLog, setSelectedLog] = useState<string | null>(null);
    const [logContent, setLogContent] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [contentLoading, setContentLoading] = useState<boolean>(false);

    useEffect(() => {
        fetchLogs();
    }, []);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/logs');
            setLogs(response.data);
        } catch (error) {
            console.error('Error fetching logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLogContent = async (filename: string) => {
        setContentLoading(true);
        setSelectedLog(filename);
        try {
            const response = await axios.get(`/api/logs/${filename}`);
            if (typeof response.data === 'object') {
                setLogContent(JSON.stringify(response.data, null, 2));
            } else {
                setLogContent(response.data);
            }
        } catch (error) {
            console.error('Error fetching log content:', error);
            setLogContent('Error loading log file.');
        } finally {
            setContentLoading(false);
        }
    };

    const formatSize = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    const downloadLog = () => {
        if (!selectedLog || !logContent) return;
        const blob = new Blob([logContent], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = selectedLog;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <Layout style={{ height: 'calc(100vh - 64px)', background: '#f0f2f5' }}>
            <Sider width={350} theme="light" style={{ overflow: 'auto', borderRight: '1px solid #e8e8e8' }}>
                <div style={{ padding: '16px', borderBottom: '1px solid #e8e8e8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={4} style={{ margin: 0 }}>Log Files</Title>
                    <Button icon={<ReloadOutlined />} onClick={fetchLogs} loading={loading} />
                </div>
                <List
                    itemLayout="horizontal"
                    dataSource={logs}
                    loading={loading}
                    renderItem={item => (
                        <List.Item 
                            style={{ 
                                padding: '12px 16px', 
                                cursor: 'pointer',
                                background: selectedLog === item.name ? '#e6f7ff' : 'transparent',
                                borderRight: selectedLog === item.name ? '3px solid #1890ff' : 'none'
                            }}
                            onClick={() => fetchLogContent(item.name)}
                        >
                            <List.Item.Meta
                                avatar={<FileTextOutlined style={{ fontSize: '20px', color: '#1890ff' }} />}
                                title={<Text strong>{item.name}</Text>}
                                description={
                                    <Space direction="vertical" size={0} style={{ fontSize: '12px' }}>
                                        <Text type="secondary">{formatSize(item.size)}</Text>
                                        <Text type="secondary">{formatDate(item.updatedAt)}</Text>
                                    </Space>
                                }
                            />
                        </List.Item>
                    )}
                />
            </Sider>
            <Content style={{ padding: '24px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {selectedLog ? (
                    <Card 
                        title={selectedLog} 
                        extra={
                            <Space>
                                <Button icon={<ReloadOutlined />} onClick={() => fetchLogContent(selectedLog)} loading={contentLoading}>Refresh</Button>
                                <Button icon={<DownloadOutlined />} onClick={downloadLog} type="primary">Download</Button>
                            </Space>
                        }
                        bodyStyle={{ padding: 0, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
                        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                    >
                        <div style={{ 
                            padding: '16px', 
                            overflow: 'auto', 
                            flex: 1, 
                            backgroundColor: '#1e1e1e', 
                            color: '#d4d4d4', 
                            fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                            fontSize: '13px',
                            whiteSpace: 'pre-wrap',
                            borderBottomLeftRadius: '8px',
                            borderBottomRightRadius: '8px'
                        }}>
                            {contentLoading ? 'Loading...' : logContent}
                        </div>
                    </Card>
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Text type="secondary" style={{ fontSize: '16px' }}>Select a log file to view its content</Text>
                    </div>
                )}
            </Content>
        </Layout>
    );
};

export default LogViewer;
